import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IcPageHeader,
  IcButton,
  IcTextField,
  IcAlert,
  IcStatusTag,
} from "@ukic/react";

import {
  api,
  liveUrl,
  type DeviceDetail,
  type Capture,
  type AgentStatus,
  type LatestUpdate,
} from "../api";
import {
  humanBytes, humanHz, humanRate, timeAgo,
  cpuStatus, tempStatus, diskStatus, healthLabel,
} from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { ModeTag, DeviceStatusTag } from "../components/Tags";
import { SdrCard } from "../components/SdrCard";
import { DecoderPackages } from "../components/DecoderPackages";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useRole } from "../useRole";
import { useToast } from "../toast";

type ConfirmAction = "reboot" | "update" | null;

export default function Device() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const toast = useToast();

  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [agent, setAgent] = useState<AgentStatus | null>(null);
  const [latest, setLatest] = useState<LatestUpdate | null>(null);

  // maintenance confirm dialog
  const [confirm, setConfirm] = useState<ConfirmAction>(null);

  // admin metadata edit
  const [editName, setEditName] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // admin recovery staging
  const [recScriptKey, setRecScriptKey] = useState("");
  const [recSha, setRecSha] = useState("");
  const [recBusy, setRecBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api
      .getDevice(id)
      .then((d) => {
        setDevice(d);
        setEditName(d.name);
        setEditLocation(d.location ?? "");
      })
      .catch(setError);
  }, [id]);

  const refreshMode = useCallback(() => {
    api.getMode(id).then(setAgent).catch(() => setAgent(null));
  }, [id]);

  useEffect(() => {
    load();
    api.getLatestUpdate().then(setLatest).catch(() => setLatest(null));
  }, [load]);

  useEffect(() => {
    refreshMode();
    const t = window.setInterval(refreshMode, 15_000);
    return () => window.clearInterval(t);
  }, [refreshMode]);

  const diskFree = agent?.disk_free ?? device?.disk_free ?? null;
  const cpu = agent?.cpu_percent ?? device?.cpu_percent ?? null;
  const temp = agent?.temp_c ?? device?.temp_c ?? null;
  const sdrs = agent?.sdrs ?? [];
  const liveSlotId = sdrs.find((s) => s.task === "live")?.id ?? null;

  const openLive = useCallback(() => {
    // Open the in-page Live view (Live is started per-SDR via the SDR card).
    if (device?.hostname) {
      const slug = device.hostname.split(".")[0].replace(/^hl-/, "");
      navigate(`/live/${slug}/`);
    } else {
      toast.error("No hostname", "This device has no live endpoint yet.");
    }
  }, [device, navigate, toast]);

  const download = useCallback(
    async (c: Capture) => {
      try {
        const { data_url } = await api.getDownload(c.id);
        // Browser pulls straight from R2 (presigned, resumable). SPEC §3.5.
        window.open(data_url, "_blank", "noopener");
      } catch (e) {
        toast.error("Download failed", e instanceof Error ? e.message : String(e));
      }
    },
    [toast],
  );

  const runConfirm = useCallback(async () => {
    const action = confirm;
    setConfirm(null);
    if (!action) return;
    try {
      if (action === "reboot") {
        await api.rebootDevice(id);
        toast.success("Reboot scheduled");
      } else {
        await api.updateAgent(id);
        toast.success(
          "Update started",
          latest?.version
            ? `Pushing ${latest.version}: download → verify → apply → restart.`
            : "OpenWebRX+ will upgrade and restart.",
        );
      }
    } catch (e) {
      toast.error("Action failed", e instanceof Error ? e.message : String(e));
    }
  }, [confirm, id, toast, latest]);

  const saveMeta = useCallback(async () => {
    setSavingMeta(true);
    try {
      const updated = await api.updateDevice(id, {
        name: editName.trim(),
        location: editLocation.trim(),
      });
      setDevice((d) => (d ? { ...d, name: updated.name, location: updated.location } : d));
      toast.success("Saved", "Device metadata updated.");
    } catch (e) {
      toast.error("Save failed", e instanceof Error ? e.message : String(e));
    } finally {
      setSavingMeta(false);
    }
  }, [id, editName, editLocation, toast]);


  const stageRecovery = useCallback(async () => {
    setRecBusy(true);
    try {
      await api.stageRecovery(id, {
        script_key: recScriptKey.trim(),
        sha256: recSha.trim(),
      });
      toast.success("Recovery staged", "The device runs it on its next recovery check-in.");
      setRecScriptKey("");
      setRecSha("");
      load();
    } catch (e) {
      toast.error("Stage failed", e instanceof Error ? e.message : String(e));
    } finally {
      setRecBusy(false);
    }
  }, [id, recScriptKey, recSha, toast, load]);

  const clearRecovery = useCallback(async () => {
    setRecBusy(true);
    try {
      await api.clearRecovery(id);
      toast.success("Recovery action cleared");
      load();
    } catch (e) {
      toast.error("Clear failed", e instanceof Error ? e.message : String(e));
    } finally {
      setRecBusy(false);
    }
  }, [id, toast, load]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!device) return <Loading label="Loading device…" />;

  const captures = device.captures ?? [];
  const currentVersion = agent?.agent_version ?? device.agent_version ?? null;
  const outOfDate =
    !!latest?.version && !!currentVersion && currentVersion !== latest.version;

  const captureCols: Column<Capture>[] = [
    { key: "name", header: "Name", cell: (c) => <code>{c.name}</code> },
    { key: "freq", header: "Freq", cell: (c) => humanHz(c.freq) },
    { key: "rate", header: "Rate", cell: (c) => humanRate(c.rate) },
    { key: "dur", header: "Duration", cell: (c) => `${c.duration_s}s` },
    { key: "type", header: "Type", cell: (c) => c.datatype },
    { key: "size", header: "Size", align: "right", cell: (c) => humanBytes(c.bytes) },
    {
      key: "status",
      header: "Status",
      cell: (c) => (
        <IcStatusTag
          label={c.status}
          status={
            c.status === "stored"
              ? "success"
              : c.status === "expired"
                ? "neutral"
                : "warning"
          }
        />
      ),
    },
    { key: "started", header: "Started", cell: (c) => timeAgo(c.started_utc) },
    {
      key: "dl",
      header: "",
      align: "right",
      cell: (c) =>
        c.status === "stored" ? (
          <IcButton size="small" variant="secondary" onClick={() => download(c)}>
            Download
          </IcButton>
        ) : (
          <span className="hl-muted">—</span>
        ),
    },
  ];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading={device.name} subheading={device.location ?? "Device"}>
        <div slot="actions" className="hl-row">
          <DeviceStatusTag device={device} />
          <ModeTag mode={agent?.mode ?? device.mode} />
        </div>
      </IcPageHeader>

      {device.status === "pending" && (
        <IcAlert
          variant="warning"
          heading="Pending approval"
          message="This device must be approved before it can go live or capture."
        />
      )}

      {device.recovery_active && (
        <IcAlert
          variant="error"
          heading="Device in recovery mode"
          message={`${device.recovery_reason ?? "Self-repairing (local first-aid + phoning home)."}${
            device.recovery_last ? ` — ${device.recovery_last}` : ""
          }`}
        />
      )}

      {/* ---- overview ---- */}
      <div className="hl-panel hl-stack">
        <h2>Overview</h2>
        <dl className="hl-meta">
          <dt>Hostname</dt>
          <dd>{device.hostname ?? "—"}</dd>
          <dt>CPU</dt>
          <dd>
            {cpu != null ? `${cpu}%` : "—"}{" "}
            <IcStatusTag className="hl-inline-tag" size="small" label={healthLabel(cpuStatus(cpu))} status={cpuStatus(cpu)} />
          </dd>
          <dt>Temp</dt>
          <dd>
            {temp != null ? `${temp} °C` : "—"}{" "}
            <IcStatusTag className="hl-inline-tag" size="small" label={healthLabel(tempStatus(temp))} status={tempStatus(temp)} />
          </dd>
          <dt>Disk free</dt>
          <dd>
            {humanBytes(diskFree)}{" "}
            <IcStatusTag className="hl-inline-tag" size="small" label={healthLabel(diskStatus(diskFree))} status={diskStatus(diskFree)} />
          </dd>
          <dt>Agent</dt>
          <dd>
            {currentVersion ?? "?"}
            {outOfDate && (
              <IcStatusTag
                className="hl-inline-tag"
                label={`update → ${latest?.version}`}
                status="warning"
                size="small"
              />
            )}
          </dd>
          <dt>OWRX+</dt>
          <dd>{agent?.owrx_version ?? device.owrx_version ?? "?"}</dd>
          <dt>Last seen</dt>
          <dd>{timeAgo(device.last_seen)}</dd>
        </dl>
      </div>

      {/* ---- SDRs (per-SDR tasks) ---- */}
      <div className="hl-stack">
        <h2>SDRs</h2>
        <p className="hl-muted">
          Each SDR runs one task; tasks on different SDRs run at the same time.
          Live is one-at-a-time. {device.hostname && (
            <a href={liveUrl(device.hostname)} target="_blank" rel="noreferrer">
              Open Live in a new tab
            </a>
          )}
        </p>
        {sdrs.length === 0 ? (
          <IcAlert
            variant="info"
            heading="No SDRs reported"
            message="The agent reported no SDRs — needs agent 0.5.0+ and a dongle attached."
          />
        ) : (
          sdrs.map((s) => (
            <SdrCard
              key={s.id}
              deviceId={id}
              slot={s}
              deviceActive={device.status === "active"}
              diskFree={diskFree}
              liveElsewhere={!!liveSlotId && liveSlotId !== s.id}
              decodersReady={agent?.decoders}
              decoderSdrs={agent?.decoder_sdrs}
              onChanged={refreshMode}
              onOpenLive={openLive}
            />
          ))
        )}
      </div>

      {/* ---- decoder packages (web-managed install/remove) ---- */}
      <DecoderPackages deviceId={id} isAdmin={isAdmin} deviceActive={device.status === "active"} />

      {/* ---- captures list ---- */}
      <div className="hl-stack">
        <h2>Captures</h2>
        <DataTable
          caption="Captures"
          columns={captureCols}
          rows={captures}
          rowKey={(c) => c.id}
          emptyMessage="No captures yet."
        />
      </div>

      {/* ---- maintenance ---- */}
      <div className="hl-panel hl-stack">
        <h2>Maintenance</h2>
        <p className="hl-muted">
          {latest?.version
            ? `Latest published release: ${latest.version}. ` +
              (outOfDate
                ? "This device is out of date — pushing the update downloads the bundle, verifies it, applies it and restarts."
                : "This device is up to date.")
            : "No update bundle has been published yet (run infra/make-update-bundle.sh)."}
        </p>
        <div className="hl-row">
          <IcButton variant="secondary" onClick={() => setConfirm("reboot")}>
            Reboot
          </IcButton>
          {isAdmin && (
            <IcButton
              variant={outOfDate ? "primary" : "secondary"}
              onClick={() => setConfirm("update")}
            >
              {outOfDate ? `Update to ${latest?.version}` : "Update software + OWRX+"}
            </IcButton>
          )}
        </div>
      </div>

      {/* ---- admin metadata ---- */}
      {isAdmin && (
        <div className="hl-panel hl-stack">
          <h2>Metadata (admin)</h2>
          <p className="hl-muted">
            Name and location are display-only; the hostname stays stable.
          </p>
          <div className="hl-row">
            <IcTextField
              label="Name"
              value={editName}
              onIcInput={(e) => setEditName((e as CustomEvent<{ value: string }>).detail.value)}
            />
            <IcTextField
              label="Location"
              value={editLocation}
              onIcInput={(e) =>
                setEditLocation((e as CustomEvent<{ value: string }>).detail.value)
              }
            />
            <IcButton
              onClick={saveMeta}
              loading={savingMeta}
              disabled={
                !editName.trim() ||
                (editName === device.name && editLocation === (device.location ?? ""))
              }
            >
              Save
            </IcButton>
          </div>
        </div>
      )}

      {/* ---- recovery (admin) ---- */}
      {isAdmin && (
        <div className="hl-panel hl-stack">
          <h2>Recovery (admin)</h2>
          <p className="hl-muted">
            For a soft-bricked device (bad upgrade / broken venv). Upload a fix
            script to R2 under <code>recovery/</code> (see{" "}
            <code>infra/stage-recovery.sh</code>), then stage its key + sha256
            here. The device downloads it, verifies the checksum, and runs it as
            root on its next recovery check-in.{" "}
            {device.recovery_staged
              ? "A script is currently staged."
              : "Nothing staged."}
          </p>
          {device.recovery_last && (
            <p className="hl-muted">Last recovery activity: {device.recovery_last}</p>
          )}
          <div className="hl-row">
            <IcTextField
              label="Script key (recovery/…)"
              value={recScriptKey}
              onIcInput={(e) =>
                setRecScriptKey((e as CustomEvent<{ value: string }>).detail.value)
              }
            />
            <IcTextField
              label="sha256"
              value={recSha}
              onIcInput={(e) => setRecSha((e as CustomEvent<{ value: string }>).detail.value)}
            />
            <IcButton
              onClick={stageRecovery}
              loading={recBusy}
              disabled={!recScriptKey.trim() || !recSha.trim()}
            >
              Stage
            </IcButton>
            {device.recovery_staged && (
              <IcButton variant="secondary" onClick={clearRecovery} loading={recBusy}>
                Clear staged
              </IcButton>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        heading={confirm === "update" ? "Update device?" : "Reboot device?"}
        message={
          confirm === "update"
            ? (latest?.version
                ? `Push release ${latest.version} to this device: download the bundle, verify its checksum, apply (agent code + OWRX+ profiles/plugins), then restart. `
                : "Upgrade OpenWebRX+ and restart. ") +
              "The device will be briefly offline."
            : "Schedule a reboot? The device will be briefly offline."
        }
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
